import React, { useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Users, FloppyDisk, Buildings } from "@phosphor-icons/react";

/**
 * Onboarding Managers — superadmin / admin / manager assigns one or more
 * states and / or districts to each `onboarding_manager` user. The user
 * can then only create / edit gig partners inside that hierarchy.
 */
export default function OnboardingManagersAdmin() {
  const [managers, setManagers] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [drafts, setDrafts] = useState({});   // { user_id: {states:[], districts:[]} }
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const [mgrs, locs] = await Promise.all([
        api.get("/gig-partners/onboarding-managers"),
        api.get("/master/locations"),
      ]);
      setManagers(mgrs.data || []);
      const l = locs.data || [];
      setStates(l.filter(x => x.type === "state"));
      setDistricts(l.filter(x => x.type === "district"));
      // Seed drafts from server.
      const d = {};
      (mgrs.data || []).forEach(m => {
        d[m.id] = {
          states: new Set(m.assigned_states || []),
          districts: new Set(m.assigned_districts || []),
        };
      });
      setDrafts(d);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const toggle = (uid, kind, id) => {
    setDrafts(prev => {
      const cur = prev[uid] || { states: new Set(), districts: new Set() };
      const target = new Set(cur[kind]);
      if (target.has(id)) target.delete(id); else target.add(id);
      return { ...prev, [uid]: { ...cur, [kind]: target } };
    });
  };

  const save = async (uid) => {
    setBusy(uid);
    try {
      const d = drafts[uid] || { states: new Set(), districts: new Set() };
      await api.post("/gig-partners/onboarding-managers/assign", {
        user_id: uid,
        assigned_states: Array.from(d.states),
        assigned_districts: Array.from(d.districts),
      });
      toast.success("Assignments saved");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(null);
  };

  return (
    <AppShell title="Onboarding Managers">
      <div data-testid="onboarding-mgrs" className="space-y-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#8A8780]">Workforce · Hierarchy</div>
          <h1 className="text-2xl font-heading mt-1 flex items-center gap-2">
            <Buildings size={22} weight="duotone"/> Onboarding Managers
          </h1>
          <p className="text-sm text-[#5C5A55] mt-1">
            Assign one or more states or districts to each manager. They'll
            only see and manage gig partners inside those regions.
          </p>
        </div>

        {managers.length === 0 && (
          <div className="p-8 bg-white rounded-lg border border-[#E5E2D9] text-center text-sm text-[#8A8780]">
            No onboarding managers yet. Create an admin user with role
            <span className="font-mono mx-1 px-1.5 py-0.5 rounded bg-[#F0EEE4]">onboarding_manager</span>
            in Staff Management → Create Staff.
          </div>
        )}

        {managers.map(m => {
          const draft = drafts[m.id] || { states: new Set(), districts: new Set() };
          return (
            <div key={m.id} className="bg-white rounded-lg border border-[#E5E2D9] overflow-hidden"
                 data-testid={`om-row-${m.id}`}>
              <div className="px-4 py-3 bg-[#F7F6F2] flex items-center justify-between gap-3">
                <div>
                  <div className="font-medium">{m.name}</div>
                  <div className="text-xs text-[#5C5A55]">
                    {m.email} {m.phone && ` · ${m.phone}`}
                  </div>
                </div>
                <button onClick={() => save(m.id)} disabled={busy === m.id}
                         data-testid={`om-save-${m.id}`}
                         className="h-9 px-3 rounded-md bg-[#2D5A27] text-white text-xs flex items-center gap-1 disabled:opacity-50">
                  <FloppyDisk size={14}/> {busy === m.id ? "Saving…" : "Save"}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                <div className="p-4 border-r border-[#E5E2D9]">
                  <div className="text-[11px] uppercase tracking-wider text-[#8A8780] mb-2">
                    States ({draft.states.size})
                  </div>
                  <div className="space-y-1 max-h-56 overflow-y-auto">
                    {states.map(s => (
                      <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-[#F7F6F2] px-2 py-1 rounded">
                        <input type="checkbox" checked={draft.states.has(s.id)}
                                onChange={() => toggle(m.id, "states", s.id)}
                                data-testid={`om-${m.id}-state-${s.id}`}/>
                        {s.name}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="p-4">
                  <div className="text-[11px] uppercase tracking-wider text-[#8A8780] mb-2">
                    Districts ({draft.districts.size})
                  </div>
                  <div className="space-y-1 max-h-56 overflow-y-auto">
                    {districts.map(d => (
                      <label key={d.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-[#F7F6F2] px-2 py-1 rounded">
                        <input type="checkbox" checked={draft.districts.has(d.id)}
                                onChange={() => toggle(m.id, "districts", d.id)}
                                data-testid={`om-${m.id}-district-${d.id}`}/>
                        {d.name}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </AppShell>
  );
}
